import { Card, Table, Button, Tag, Spin } from 'antd';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getSafeHavenTransferStatus,
	clearSafeHavenResult,
} from '../../methods/redux/reducers/payment-gateway';

const StyledCard = styled(Card)`
	.ant-card-body {
		padding: 24px;
	}
`;

const StatusTag = styled(Tag)`
	font-weight: 500;
	padding: 4px 12px;
	border-radius: 4px;
`;

const StyledTable = styled(Table)`
	.table-row-light {
		background-color: #ffffff;
	}
	.table-row-dark {
		background-color: #fafafa;
	}
	.ant-table-tbody > tr:hover > td {
		background-color: #e6f7ff !important;
	}
`;

const SafeHavenTransferStatusDetails = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { sessionId } = useParams<{ sessionId: string }>();
	const { safeHavenResult } = useAppSelector(
		(store) => store.paymentGateway
	) as { safeHavenResult: any };

	useEffect(() => {
		if (sessionId) {
			dispatch(getSafeHavenTransferStatus(sessionId));
		}
		return () => {
			dispatch(clearSafeHavenResult());
		};
	}, [sessionId, dispatch]);

	const handleBack = () => {
		navigate('/core-modules/virtual-fiat-accounts');
	};

	if (!safeHavenResult) {
		return (
			<StyledCard>
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
					<p style={{ marginTop: '16px' }}>
						Loading transfer status details...
					</p>
				</div>
			</StyledCard>
		);
	}

	const transferData = safeHavenResult.data?.data;

	// Prepare table data
	const tableData = [
		{
			key: 'id',
			label: 'ID',
			value: transferData?._id || 'N/A',
			type: 'text',
		},
		{
			key: 'sessionId',
			label: 'Session ID',
			value: transferData?.sessionId || 'N/A',
			type: 'text',
		},
		{
			key: 'amount',
			label: 'Amount',
			value: transferData?.amount
				? new Intl.NumberFormat('en-NG', {
						style: 'currency',
						currency: 'NGN',
				  }).format(transferData.amount)
				: 'N/A',
			type: 'currency',
		},
		{
			key: 'fees',
			label: 'Fees',
			value: transferData?.fees
				? new Intl.NumberFormat('en-NG', {
						style: 'currency',
						currency: 'NGN',
				  }).format(transferData.fees)
				: 'N/A',
			type: 'currency',
		},
		{
			key: 'status',
			label: 'Status',
			value: transferData?.status || 'N/A',
			type: 'status',
		},
		{
			key: 'creditAccountNumber',
			label: 'Credit Account Number',
			value: transferData?.creditAccountNumber || 'N/A',
			type: 'text',
		},
		{
			key: 'debitAccountName',
			label: 'Debit Account Name',
			value: transferData?.debitAccountName || 'N/A',
			type: 'text',
		},
		{
			key: 'debitAccountNumber',
			label: 'Debit Account Number',
			value: transferData?.debitAccountNumber || 'N/A',
			type: 'text',
		},
		{
			key: 'narration',
			label: 'Narration',
			value: transferData?.narration || 'N/A',
			type: 'text',
		},
		{
			key: 'isReversed',
			label: 'Is Reversed',
			value: transferData?.isReversed ? 'Yes' : 'No',
			type: 'boolean',
		},
		{
			key: 'isDeleted',
			label: 'Is Deleted',
			value: transferData?.isDeleted ? 'Yes' : 'No',
			type: 'boolean',
		},
		{
			key: 'createdAt',
			label: 'Created At',
			value: transferData?.createdAt
				? new Date(transferData.createdAt).toLocaleString()
				: 'N/A',
			type: 'date',
		},
	];

	const columns = [
		{
			title: 'Field',
			dataIndex: 'label',
			key: 'label',
			width: '30%',
			render: (text: string) => (
				<strong style={{ color: '#1890ff' }}>{text}</strong>
			),
		},
		{
			title: 'Value',
			dataIndex: 'value',
			key: 'value',
			width: '70%',
			render: (text: string, record: any) => {
				if (record.type === 'status') {
					const statusColors: any = {
						success: 'green',
						pending: 'orange',
						failed: 'red',
						completed: 'green',
						processing: 'blue',
						cancelled: 'default',
					};
					const color = statusColors[text?.toLowerCase()] || 'default';
					return (
						<StatusTag color={color}>{text?.toUpperCase() || 'N/A'}</StatusTag>
					);
				}
				if (record.type === 'boolean') {
					return (
						<Tag color={text === 'Yes' ? 'orange' : 'green'}>{text}</Tag>
					);
				}
				return <span style={{ wordBreak: 'break-word' }}>{text}</span>;
			},
		},
	];

	return (
		<StyledCard>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div>
					<h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
						Safe Haven Transfer Status Details
					</h1>
					<p style={{ margin: '8px 0 0 0', color: '#666' }}>
						Session ID: {sessionId}
					</p>
				</div>
				<Button onClick={handleBack}>Back to List</Button>
			</div>

			{(safeHavenResult as any)?.status && (
				<div
					style={{
						marginBottom: '24px',
						padding: '12px',
						background: '#f0f8ff',
						borderRadius: '4px',
					}}
				>
					<p style={{ margin: '4px 0' }}>
						<strong>Response Status:</strong>{' '}
						<Tag
							color={
								(safeHavenResult as any).status === 'success'
									? 'green'
									: 'orange'
							}
						>
							{(safeHavenResult as any).status}
						</Tag>
					</p>
					{(safeHavenResult as any)?.status_code && (
						<p style={{ margin: '4px 0' }}>
							<strong>Status Code:</strong>{' '}
							{(safeHavenResult as any).status_code}
						</p>
					)}
					{(safeHavenResult as any)?.data?.message && (
						<p style={{ margin: '4px 0' }}>
							<strong>Message:</strong>{' '}
							{(safeHavenResult as any).data.message}
						</p>
					)}
				</div>
			)}

			<StyledTable
				columns={columns}
				dataSource={tableData}
				pagination={false}
				bordered
				style={{
					backgroundColor: '#fff',
					borderRadius: '8px',
					overflow: 'hidden',
				}}
				rowClassName={(record, index) =>
					index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
				}
			/>
		</StyledCard>
	);
};

export default SafeHavenTransferStatusDetails;
